import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { getToken } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Loader2, ChevronLeft, Download, ShieldAlert, Zap, RotateCcw,
  UserX, TimerReset, RefreshCw, ExternalLink, ShieldOff, Eye,
} from "lucide-react";
import { toast } from "sonner";
import BlockActorDialog from "../components/BlockActorDialog";
import ActorListsPanel from "../components/ActorListsPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Category → visual + copy config. Kept in one place so a future 5th category
 * (e.g., IP-blocklist hits) becomes a single object entry, not a scatter-shot
 * of if-branches through the JSX.
 */
const CATEGORY_META = {
  drift_alerts: {
    icon: Zap,
    label: "Drift alerts",
    subtitle: "Sensitive integration keys or webhook secrets changed",
    accent: "amber",
    audit_event: "settings.update",
  },
  restore_actions: {
    icon: RotateCcw,
    label: "Restore actions",
    subtitle: "Settings reverted via history rollback",
    accent: "violet",
    audit_event: "settings.history.restore",
  },
  failed_auth: {
    icon: UserX,
    label: "Failed authentications",
    subtitle: "Anonymous or 401-rejected API calls",
    accent: "rose",
    audit_event: null,  // no exact event — actor filter is enough
  },
  rate_limit_hits: {
    icon: TimerReset,
    label: "Rate-limit hits",
    subtitle: "429 responses — magic-link floods or brute-force attempts",
    accent: "sky",
    audit_event: null,
  },
  block_hits: {
    icon: ShieldOff,
    label: "Block-list hits",
    subtitle: "403 rejections from your actor block list — enforcement fired",
    accent: "slate",
    audit_event: "actor.blocked_request",
  },
};

// Tailwind palette lookup — avoids inline hex color strings scattered around.
const ACCENT = {
  amber:  { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-800",  ring: "ring-amber-200",  bar: "#F59E0B" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-800", ring: "ring-violet-200", bar: "#8B5CF6" },
  rose:   { bg: "bg-rose-50",   border: "border-rose-200",   text: "text-rose-800",   ring: "ring-rose-200",   bar: "#F43F5E" },
  sky:    { bg: "bg-sky-50",    border: "border-sky-200",    text: "text-sky-800",    ring: "ring-sky-200",    bar: "#0EA5E9" },
  slate:  { bg: "bg-slate-50",  border: "border-slate-300",  text: "text-slate-800",  ring: "ring-slate-300",  bar: "#475569" },
};

/**
 * Inline SVG sparkline. Purposely tiny — the whole security dashboard renders
 * in one round-trip, so a full chart library would be overkill.
 * `data` is [{date, count}, ...] oldest-first.
 */
function Sparkline({ data, color = "#0EA5E9", width = 140, height = 40 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map(d => d.count));
  const step = width / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = i * step;
    const y = height - (d.count / max) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // Area fill for visual weight even when counts are 0
  const area = `0,${height} ${points} ${(data.length - 1) * step},${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden="true">
      <polygon points={area} fill={color} opacity="0.12"/>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round"/>
      {data.map((d, i) => (
        <circle key={i} cx={i * step} cy={height - (d.count / max) * (height - 4) - 2} r="1.6" fill={color}/>
      ))}
    </svg>
  );
}

/**
 * Single category card. Big number + spark + top actors + recent-5 rows.
 * Follows the same visual grammar as the History panel / Audit log so the
 * three admin surfaces feel coherent.
 */
function CategoryCard({ kind, meta, data, onDrillDown, onBlock, onWatch }) {
  const Icon = meta.icon;
  const c = ACCENT[meta.accent] || ACCENT.sky;
  const empty = !data || data.count === 0;
  // Actionable categories = anywhere with a real actor/IP we can lock down.
  // Drift/restore are internal admin actions — no block button makes sense there.
  const actionable = kind === "failed_auth" || kind === "rate_limit_hits" || kind === "block_hits";

  return (
    <Card className={`p-5 shadow-sm border ${c.border} ${c.bg}`} data-testid={`security-card-${kind}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon className={c.text} size={20}/>
          <div>
            <h3 className={`text-base font-semibold ${c.text}`}>{meta.label}</h3>
            <p className="text-[11px] text-slate-600 mt-0.5">{meta.subtitle}</p>
          </div>
        </div>
        <Sparkline data={data?.spark || []} color={c.bar}/>
      </div>

      <div className="flex items-end gap-2 mb-3">
        <div className={`text-4xl font-bold leading-none ${c.text}`} data-testid={`security-count-${kind}`}>
          {data?.count ?? 0}
        </div>
        <div className="text-xs text-slate-500 pb-1">event(s)</div>
      </div>

      {/* Top actors */}
      {data?.top_actors?.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wide font-medium text-slate-500 mb-1">Top actors</div>
          <div className="flex flex-wrap gap-1">
            {data.top_actors.map((a) => (
              <button
                key={a.actor_email}
                onClick={() => onDrillDown(a.actor_email, null)}
                className="text-[11px] px-2 py-0.5 rounded-full bg-white/70 border border-slate-200 hover:bg-white hover:border-slate-300 text-slate-700"
                data-testid={`security-actor-${kind}-${a.actor_email}`}
                title={`See ${a.actor_email}'s activity in the audit log`}
              >
                <span className="font-medium">{a.actor_email}</span>
                <span className="text-slate-500 ml-1">({a.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent rows */}
      <div>
        <div className="text-[10px] uppercase tracking-wide font-medium text-slate-500 mb-1">Recent</div>
        {empty ? (
          <div className="text-[11px] text-slate-500 py-2 italic" data-testid={`security-empty-${kind}`}>
            No {meta.label.toLowerCase()} in this window.
          </div>
        ) : (
          <ul className="space-y-1" data-testid={`security-recent-${kind}`}>
            {data.recent.map((r) => (
              <li key={r.id} className="bg-white/60 border border-slate-200 rounded p-2 text-[11px]" data-testid={`security-row-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600 font-mono">
                    {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                  </span>
                  <div className="flex items-center gap-2">
                    {actionable && r.actor && r.actor !== "unknown" && (
                      <>
                        <button
                          onClick={() => onBlock({ email: r.actor === "anonymous" ? "" : r.actor, ip: r.ip || "" })}
                          className="text-rose-500 hover:text-rose-700"
                          title={`Block ${r.actor}${r.ip ? " / "+r.ip : ""}`}
                          data-testid={`security-block-btn-${r.id}`}
                        >
                          <ShieldOff size={11}/>
                        </button>
                        <button
                          onClick={() => onWatch({ email: r.actor === "anonymous" ? "" : r.actor, ip: r.ip || "" })}
                          className="text-sky-500 hover:text-sky-700"
                          title={`Watch ${r.actor}${r.ip ? " / "+r.ip : ""}`}
                          data-testid={`security-watch-btn-${r.id}`}
                        >
                          <Eye size={11}/>
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => onDrillDown(r.actor, meta.audit_event)}
                      className="text-slate-400 hover:text-slate-800"
                      title="Open in audit log"
                      data-testid={`security-drill-${r.id}`}
                    >
                      <ExternalLink size={11}/>
                    </button>
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                  <span className="font-medium text-slate-800">{r.actor}</span>
                  {r.ip && (
                    <code className="text-[10px] font-mono bg-slate-100 border border-slate-200 rounded px-1 text-slate-700">{r.ip}</code>
                  )}
                  {r.path && (
                    <code className="text-[10px] font-mono bg-slate-100 border border-slate-200 rounded px-1 text-slate-700">{r.path}</code>
                  )}
                  {r.status_code != null && (
                    <span className={`text-[10px] font-mono px-1 rounded ${
                      r.status_code >= 500 ? "bg-rose-100 text-rose-700"
                      : r.status_code >= 400 ? "bg-amber-100 text-amber-800"
                      : "bg-emerald-100 text-emerald-700"
                    }`}>{r.status_code}</span>
                  )}
                </div>
                {r.sensitive_keys?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.sensitive_keys.slice(0, 3).map((k) => (
                      <code key={k} className="text-[10px] font-mono bg-amber-100 border border-amber-300 text-amber-900 rounded px-1">{k}</code>
                    ))}
                    {r.sensitive_keys.length > 3 && (
                      <span className="text-[10px] text-slate-500">+{r.sensitive_keys.length - 3}</span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

export default function AdminSecurityEvents() {
  const nav = useNavigate();
  const [windowDays, setWindowDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [blockDialog, setBlockDialog] = useState(null); // {email, ip, kind}
  const [panelRefreshKey, setPanelRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/security-events/summary?window_days=${windowDays}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load security events");
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const goAuditLog = (actor, event) => {
    const qs = new URLSearchParams();
    if (actor) qs.set("actor", actor);
    if (event) qs.set("event", event);
    nav(`/admin/audit-log${qs.toString() ? `?${qs}` : ""}`);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await fetch(`${API}/security-events/export.csv?window_days=${windowDays}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sentry-security-events-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Security events CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Overall summary — total events across all categories
  const grandTotal = useMemo(() => {
    if (!data) return 0;
    return Object.keys(CATEGORY_META).reduce((sum, k) => sum + (data[k]?.count || 0), 0);
  }, [data]);

  const openBlock = ({ email, ip }) => setBlockDialog({ email, ip, kind: "block" });
  const openWatch = ({ email, ip }) => setBlockDialog({ email, ip, kind: "watch" });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col" data-testid="security-events-page">
      <BrandHeader showSettings showAuditLog showImport showClients showLogout>
        <Button variant="ghost" onClick={() => nav("/admin")} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="back-to-admin-btn">
          <ChevronLeft size={16} className="mr-1"/>Back
        </Button>
      </BrandHeader>

      <div className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <ShieldAlert className="text-brand-primary" size={28}/>
              Security events
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              A single-glance view of anything worth investigating on your Sentry Cloud IT tenant.
              Pulls from the audit log + settings history — no separate event stream, so what you
              see here always matches the underlying data.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-600">Window:</label>
              <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
                <SelectTrigger className="w-28" data-testid="security-window-select">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7d</SelectItem>
                  <SelectItem value="30">Last 30d</SelectItem>
                  <SelectItem value="90">Last 90d</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              className="border-slate-300 text-slate-700"
              data-testid="security-refresh-btn"
            >
              <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/>Refresh
            </Button>
            <Button
              onClick={exportCsv}
              disabled={exporting}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white"
              data-testid="security-export-csv-btn"
            >
              {exporting ? <><Loader2 className="animate-spin mr-2" size={14}/>Exporting…</> : <><Download size={14} className="mr-2"/>Export CSV</>}
            </Button>
          </div>
        </div>

        {/* Grand total banner */}
        <Card className="mb-6 p-5 border-slate-200 shadow-sm" data-testid="security-summary-banner">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-5xl font-bold text-brand-primary leading-none" data-testid="security-grand-total">{grandTotal}</div>
              <div className="text-xs text-slate-500 mt-1">total events over last {windowDays} days</div>
            </div>
            <div className="flex-1 grid grid-cols-4 gap-3">
              {Object.entries(CATEGORY_META).map(([kind, meta]) => {
                const c = ACCENT[meta.accent];
                const count = data?.[kind]?.count ?? 0;
                return (
                  <div key={kind} className={`p-2 rounded border ${c.border} ${c.bg}`}>
                    <div className={`text-xl font-bold ${c.text}`}>{count}</div>
                    <div className="text-[10px] text-slate-600">{meta.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Category cards */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="animate-spin mr-2" size={20}/>Loading security events…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="security-cards-grid">
              {Object.entries(CATEGORY_META).map(([kind, meta]) => (
                <CategoryCard
                  key={kind}
                  kind={kind}
                  meta={meta}
                  data={data?.[kind]}
                  onDrillDown={goAuditLog}
                  onBlock={openBlock}
                  onWatch={openWatch}
                />
              ))}
            </div>
            {/* Blocked & watched actors panel — full width below the cards */}
            <ActorListsPanel key={panelRefreshKey}/>
          </>
        )}
      </div>

      <BlockActorDialog
        open={!!blockDialog}
        onClose={() => setBlockDialog(null)}
        prefillEmail={blockDialog?.email || ""}
        prefillIp={blockDialog?.ip || ""}
        defaultKind={blockDialog?.kind || "block"}
        onSaved={() => setPanelRefreshKey((k) => k + 1)}
      />

      <BrandFooter/>
    </div>
  );
}
